/**
 * Map Tile Layer Configuration
 *
 * Centralized configuration for map tile providers used across the application.
 * This eliminates duplication and ensures consistency between:
 * - MapLocationPicker (dashboard)
 * - MapOverlay (viewer)
 * - UIManager stage selector (viewer)
 */

export type MapStyle = 'streets' | 'satellite' | 'terrain' | 'dark' | 'light';

export interface TileLayerConfig {
  url: string;
  attribution: string;
}

/**
 * Available map tile layer configurations
 *
 * Uses free providers:
 * - OpenStreetMap for streets
 * - Esri for satellite imagery
 * - OpenTopoMap for terrain
 * - CARTO for dark/light themes
 */
export const TILE_LAYERS: Record<MapStyle, TileLayerConfig> = {
  streets: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
  },
  light: {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
  },
};

/**
 * Get tile layer config for a given style, with fallback to streets
 */
export function getTileLayerConfig(style: string): TileLayerConfig {
  return TILE_LAYERS[style as MapStyle] || TILE_LAYERS.streets;
}

/**
 * Default map center (center of USA) when no coordinates are available
 */
export const DEFAULT_MAP_CENTER = { lat: 39.8, lng: -98.5 };

/**
 * Default zoom levels
 */
export const DEFAULT_ZOOM = {
  world: 4,
  country: 6,
  region: 8,
  city: 12,
  neighborhood: 15,
  building: 18,
};
